import { api } from "@/lib/api";

// Convert a URL-safe base64 VAPID public key to a Uint8Array
export function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) output[i] = raw.charCodeAt(i);
  return output;
}

export function isPushSupported() {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export function isIOSStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    ("standalone" in window.navigator && window.navigator.standalone)
  );
}

export function isIOS() {
  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
}

export async function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
    return reg;
  } catch (e) {
    console.warn("Service worker registration failed:", e);
    return null;
  }
}

export async function getSubscriptionStatus() {
  if (!isPushSupported()) return { supported: false };
  const reg = await navigator.serviceWorker.getRegistration("/");
  if (!reg) return { supported: true, subscribed: false, permission: Notification.permission };
  const sub = await reg.pushManager.getSubscription();
  return {
    supported: true,
    subscribed: !!sub,
    permission: Notification.permission,
    endpoint: sub?.endpoint,
  };
}

export async function subscribeToPush() {
  if (!isPushSupported()) throw new Error("Push not supported on this device/browser.");
  if (isIOS() && !isIOSStandalone()) {
    throw new Error("On iPhone/iPad, add Call Swiper to your Home Screen and open from there first.");
  }
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error("Notification permission was denied.");
  }
  const reg = (await navigator.serviceWorker.getRegistration("/")) || (await registerServiceWorker());
  await navigator.serviceWorker.ready;

  const { data } = await api.get("/push/vapid-public-key");
  const applicationServerKey = urlBase64ToUint8Array(data.publicKey);

  let subscription = await reg.pushManager.getSubscription();
  if (!subscription) {
    subscription = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey,
    });
  }
  await api.post("/push/subscribe", { subscription: subscription.toJSON() });
  return { ok: true };
}

export async function unsubscribeFromPush() {
  const reg = await navigator.serviceWorker.getRegistration("/");
  if (!reg) return { ok: true };
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return { ok: true };
  try {
    await api.post("/push/unsubscribe", { endpoint: sub.endpoint });
  } catch (e) {
    /* ignore */
  }
  await sub.unsubscribe();
  return { ok: true };
}

export async function sendTestPush() {
  const { data } = await api.post("/push/test");
  return data;
}
